import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Users, Calendar, TrendingUp, AlertTriangle, Star, BarChart3 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/statistics`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("Error loading statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-300">Laden...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-300">Geen statistieken beschikbaar</div>
      </div>
    );
  }

  const maxReservations = Math.max(...Object.values(stats.reservations_by_day || {}), 1);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="statistics-view">
      <div className="p-6 border-b border-white/10">
        <h2 className="text-2xl font-serif text-gray-300" data-testid="statistics-title">Statistieken</h2>
        <p className="text-sm text-gray-400">Overzicht van reserveringen en prestaties</p>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-gold" />
                Vandaag
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-brand-gold" data-testid="today-reservations">{stats.today_reservations}</p>
              <p className="text-xs text-gray-500">reserveringen</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                Deze Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-400" data-testid="week-reservations">{stats.reservations_this_week}</p>
              <p className="text-xs text-gray-500">reserveringen</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                Deze Maand
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-400" data-testid="month-reservations">{stats.reservations_this_month}</p>
              <p className="text-xs text-gray-500">reserveringen</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-400" />
                Totaal Gasten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-purple-400" data-testid="total-guests">{stats.total_guests}</p>
              <p className="text-xs text-gray-500">in database</p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                VIP Gasten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-yellow-400">{stats.vip_guests}</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                No-Show Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-400" data-testid="noshow-rate">{stats.no_show_rate}%</p>
              <p className="text-xs text-gray-500">{stats.total_noshows} totaal no-shows</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" />
                Gem. Groepsgrootte
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-cyan-400">{stats.avg_party_size}</p>
              <p className="text-xs text-gray-500">personen per reservering</p>
            </CardContent>
          </Card>
        </div>

        {/* Reservations by Day Chart */}
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-gray-300">Reserveringen per Dag (laatste 30 dagen)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.reservations_by_day || {}).map(([day, count]) => (
                <div key={day} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-gray-400">{day}</div>
                  <div className="flex-1 bg-[#222222] rounded-full h-6 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-brand-gold to-yellow-500 rounded-full transition-all duration-500"
                      style={{ width: `${(count / maxReservations) * 100}%` }}
                    />
                  </div>
                  <div className="w-12 text-right text-sm font-semibold text-gray-300">{count}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Guests by Day */}
        <Card className="bg-[#1A1A1A] border-white/10 mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-gray-300">Gasten per Dag (laatste 30 dagen)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {Object.entries(stats.guests_by_day || {}).map(([day, count]) => (
                <div key={day} className="text-center p-3 bg-[#222222] rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">{day.slice(0, 2)}</p>
                  <p className="text-xl font-bold text-brand-gold">{count}</p>
                  <p className="text-xs text-gray-500">gasten</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Statistics;
